import axios from "axios";
import { apiLabelUrl } from "../Functions/functions";

/**
 * Fetch price for a library product via the product-price API,
 * then store it on the product via PUT.
 *
 * Returns the updated product (with price), or the original product if price fetch fails.
 * Skips custom products (always "On request").
 */
/**
 * @param {object} product
 * @param {object} [options]
 * @param {boolean} [options.forceRefetch] - Skip the fixed-price guard and always fetch from API.
 *   Use this when re-fetching discount_multiplier for an updated quantity.
 */
export default async function fetchLibraryProductPrice(product, { forceRefetch = false } = {}) {
    // Fixed price set by admin — don't fetch dynamically (unless caller forces a refetch for discount)
    if (!forceRefetch && product.price !== null && product.price !== undefined) {
        if (product.ignore_staffel_discount) {
            return { ...product, discount_multiplier: 1 };
        }
        return product;
    }

    // Need model_id and dimensions to calculate price
    if (!product.model_id) {
        return product;
    }

    try {
        const params = new URLSearchParams();
        params.set("model_id", product.model_id);
        params.set("amount", product.default_quantity || 1);
        params.set("skip_price_range", "1");

        // Map all settings to query params (must match existing order flow)
        const s = product.settings;
        const settingsMap = {
            application: s.application,
            material: s.material,
            color: s.color,
            polish: s.polish,
            coating: s.coating,
            orientation: s.orientation,
            sterile: s.sterile,
            intendedUse: s.intendedUse,
            clinicalUse: s.clinicalUse,
            custom: s.custom,
            case: s.case || "-",
            practitioner: s.practitioner || "-",
        };
        for (const [key, val] of Object.entries(settingsMap)) {
            params.set(key, val !== null && val !== undefined && val !== "" ? val : "null");
        }

        const baseUrl = await apiLabelUrl("api");
        const response = await axios.get(`${baseUrl}/product-price?${params.toString()}`);

        let unitPrice = null;
        let discountMultiplier = 1;
        if (response.data?.data && Array.isArray(response.data.data)) {
            const match = response.data.data.find((item) => item.model_id === product.model_id);
            if (match && match.unit_price !== undefined && match.unit_price !== null) {
                // "On request" or 0 means no automatic price
                if (match.unit_price === 0 || match.unit_price === "On request") {
                    unitPrice = null;
                } else {
                    unitPrice = match.unit_price;
                    discountMultiplier = match.discount_multiplier ?? 1;
                }
            }
        }

        // Product-level override: ignore staffel discount
        if (product.ignore_staffel_discount) {
            discountMultiplier = 1;
        }

        // Return price and discount_multiplier for UI display only — not persisted to DB
        return { ...product, price: unitPrice, discount_multiplier: discountMultiplier };
    } catch (err) {
        console.error("Failed to fetch library product price:", err);
    }

    return product;
}
